import type { FabricEditor } from 'modules/fabric-editor';
import { isPresetTemplate } from 'modules/manager/domain/template-access';
import { selectActiveTemplate } from 'modules/manager/store';
import { useCallback } from 'react';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { useAppSelector } from 'store/hooks';

import { useEditorDirty } from './use-editor-dirty';

export interface UseResetTemplateResult {
  /** Discard every unsaved edit, as one undoable step. */
  reset: () => void;
  canReset: boolean;
}

/**
 * "Discard unsaved changes": puts the canvas back to the template as it is
 * stored (`activeTemplate.objects` — the payload the last successful save wrote
 * back into the slice, or the fetched one if nothing was saved yet).
 *
 * The revert is applied through `history.replaceState`, i.e. as a *forward*
 * history step rather than a rewind, so Ctrl+Z undoes the reset itself and the
 * discarded work comes back. That is also why this needs no confirmation
 * dialog — nothing here is lost irrecoverably while the editor stays open.
 */
export function useResetTemplate(editor: FabricEditor | null): UseResetTemplateResult {
  const template = useAppSelector(selectActiveTemplate);
  const isDirty = useEditorDirty(editor);
  const isAdmin = useIsAdmin();

  // Same gate as saving: a regular user viewing a global preset edits nothing,
  // so there is nothing for them to discard either.
  const canEdit = template ? !isPresetTemplate(template.access) || isAdmin : false;

  const reset = useCallback(() => {
    if (!editor || !template) return;
    if (!canEdit) return;
    if (!editor.dirty?.isDirty()) return;
    // No history plugin means no undoable revert — silently reloading the
    // canvas would be a one-way data loss, so do nothing instead. The editor
    // page always installs it (see PLUGINS in media-manager-editor-page).
    const history = editor.history;
    if (!history) return;

    // The current selection points at objects that are about to be removed;
    // drop it so the inspector does not keep stale references.
    editor.selection?.clear();
    history.replaceState({ version: template.version, objects: template.objects ?? [] }, () => {
      // The canvas now equals what is stored — nothing left to save. A later
      // undo re-adds objects, which flips the flag back on its own.
      editor.dirty?.markClean();
    });
  }, [editor, template, canEdit]);

  return { reset, canReset: canEdit && isDirty && editor !== null && template !== null };
}
